import {useRef, useState, useEffect, useCallback} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import Tesseract from 'tesseract.js'
import {pdfjs} from 'react-pdf'
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry'
import {ROUTES} from '../../../../../_gori/constants'
import {LocationState} from '../../../../../types/locationTypes'
import {isValidGoriSignatureHash} from '../../utils/validation'
import {UploadArea} from './UploadArea'
import {ReviewingState} from './ReviewingState'
import {useFileProcessor} from './FileProcessor'

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker

export function GoriSignatureUploadPhoto() {
  const navigate = useNavigate()
  const location = useLocation() as {state?: LocationState}
  const intl = useIntl()
  const hash = location.state?.hash

  const fileInputRef = useRef<HTMLInputElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isReviewing, setIsReviewing] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [workerReady, setWorkerReady] = useState(false)
  const isMountedRef = useRef(true)
  const workerRef = useRef<Tesseract.Worker | null>(null)

  useEffect(() => {
    if (!hash || !isValidGoriSignatureHash(hash)) {
      navigate(ROUTES.GORI_SIGNATURE.BASE)
      return
    }

    const initWorker = async () => {
      const worker = await Tesseract.createWorker('eng')
      workerRef.current = worker
      setWorkerReady(true)
    }
    initWorker()

    return () => {
      isMountedRef.current = false
      if (workerRef.current) {
        workerRef.current.terminate()
        workerRef.current = null
      }
    }
  }, [hash, navigate])

  // Add 15-second timeout for OCR processing
  const ocrTimeoutRef = useRef<number | null>(null)
  const processingAbortedRef = useRef(false)

  const {handleFile} = useFileProcessor(
    hash,
    workerRef,
    canvasRef,
    isMountedRef,
    setIsReviewing,
    processingAbortedRef
  )

  const handleFileWithTimeout = useCallback(
    async (file: File) => {
      // Clear any existing timeout
      if (ocrTimeoutRef.current) {
        clearTimeout(ocrTimeoutRef.current)
        ocrTimeoutRef.current = null
      }

      // Reset abort flag
      processingAbortedRef.current = false

      // Set 15-second timeout
      ocrTimeoutRef.current = window.setTimeout(() => {
        if (isMountedRef.current && !processingAbortedRef.current) {
          processingAbortedRef.current = true
          setIsReviewing(false)
          toast.error(intl.formatMessage({id: 'GORI_SIGNATURE_OCR_TIMEOUT'}))
          navigate(`${ROUTES.GORI_SIGNATURE.BASE}/${hash}`, {
            state: {from: 'upload', timeout: true},
          })
        }
      }, 15000)

      try {
        await handleFile(file)
        // Clear timeout if processing completes successfully
        if (ocrTimeoutRef.current && !processingAbortedRef.current) {
          clearTimeout(ocrTimeoutRef.current)
          ocrTimeoutRef.current = null
        }
      } catch (error) {
        // Clear timeout on error
        if (ocrTimeoutRef.current && !processingAbortedRef.current) {
          clearTimeout(ocrTimeoutRef.current)
          ocrTimeoutRef.current = null
        }
        // Don't throw if we already aborted
        if (!processingAbortedRef.current) {
          throw error
        }
      }
    },
    [handleFile, hash, intl, navigate, isMountedRef, setIsReviewing]
  )

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (ocrTimeoutRef.current) {
        clearTimeout(ocrTimeoutRef.current)
        ocrTimeoutRef.current = null
      }
    }
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (files && files.length > 0) {
        await handleFileWithTimeout(files[0])
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [handleFileWithTimeout]
  )

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)

      const files = e.dataTransfer.files
      if (files && files.length > 0) {
        await handleFileWithTimeout(files[0])
      }
    },
    [handleFileWithTimeout]
  )

  return (
    <div className='gori-signature-upload-photo'>
      <div className='gori-signature-header-wrapper text-center'>
        <img
          src='/media/gori/gori-signature/gori-signature-black.svg'
          alt={intl.formatMessage({id: 'GORI_SIGNATURE_TITLE'})}
          className='gori-signature-logo mb-5'
        />
        <p className='text-dark'>
          {intl.formatMessage({id: 'GORI_SIGNATURE_UPLOAD_PHOTO_INSTRUCTION'})}{' '}
          <span
            className='gori-signature-tutorial__inline-link text-decoration-underline cursor-pointer'
            onClick={() =>
              navigate(ROUTES.GORI_SIGNATURE.METHOD_SELECT, {
                state: {hash: hash},
              })
            }
          >
            {intl.formatMessage({id: 'GORI_SIGNATURE_TRY_DIFFERENT_METHOD'})}
          </span>
        </p>
      </div>
      <div className='gori-signature-upload-card'>
        {!isReviewing ? (
          <UploadArea
            disabled={!workerReady}
            isDragging={isDragging}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onUploadClick={() => fileInputRef.current?.click()}
            fileInputRef={fileInputRef}
            onFileSelect={handleFileSelect}
          />
        ) : (
          <ReviewingState />
        )}
      </div>
      <canvas ref={canvasRef} className='d-none' />
    </div>
  )
}
